import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';
import { buildOfficialHabitatCheckoutHandoffUrl, currentHabitatPaymentPeriod } from '../../../lib/officialCheckoutHandoff';
import { 
  Home, FileText, Clock, CheckCircle, Shield, UploadCloud, MapPin, 
  Calendar, ArrowLeft, Lock, User, FileCheck, Trash2, Heart, 
  ArrowRight, BedDouble, Bath, Activity, CreditCard, AlertTriangle, 
  MessageSquare, Send, X, Save, EyeOff, Eye, FileDown, ShieldCheck, 
  Search, Wrench, PlusCircle, Map, Loader2, Navigation, Truck 
} from 'lucide-react';
import Header from '../../../components/Header';
import InteractiveMap from '../../../components/InteractiveMap';
import NotificationBell from '../components/NotificationBell'; // ✅ Ajout de la cloche de notifications

export default function TenantDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [activeTab, setActiveTab] = useState<'applications' | 'documents' | 'favorites' | 'profil' | 'contrats' | 'maintenance' | 'carte' | 'suivi'>('applications');
  
  const [applications, setApplications] = useState<any[]>([]);
  const [contracts, setContracts] = useState<any[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [favoriteListings, setFavoriteListings] = useState<any[]>([]);
  
  const [pendingScreenings, setPendingScreenings] = useState<any[]>([]);

  const [tickets, setTickets] = useState<any[]>([]);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);
  const [ticketForm, setTicketForm] = useState({
    listing_id: '', category: 'plomberie', urgency: 'normale', description: ''
  });

  const [trackingTicket, setTrackingTicket] = useState<any>(null);

  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [processingAuthId, setProcessingAuthId] = useState<string | null>(null);
  const [isPayingRent, setIsPayingRent] = useState<string | null>(null);
  const [chatAppId, setChatAppId] = useState<string | null>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');

  const [profileData, setProfileData] = useState({
    full_name: 'Locataire', phone: '', address: '', city: '', country: 'Canada', postal_code: '', is_address_private: true
  });
  const [savingProfile, setSavingProfile] = useState(false);

  const idFileInput = useRef<HTMLInputElement>(null);
  const incomeFileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchData();
    checkStripeRedirect(); 
    
    const channel = supabase
      .channel('tenant_maintenance_tracking')
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'habitat_maintenance_tickets' },
        (payload) => {
          setTickets((currentTickets) => 
            currentTickets.map(t => t.id === payload.new.id ? { ...t, ...payload.new } : t)
          );
          setTrackingTicket((currentTracking: any) => {
            if (currentTracking && currentTracking.id === payload.new.id) {
              return { ...currentTracking, ...payload.new };
            }
            return currentTracking;
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  useEffect(() => {
    let interval: any;
    if (activeTab === 'maintenance' || activeTab === 'suivi') {
      interval = setInterval(() => {
        fetchTicketsOnly();
      }, 10000); 
    }
    return () => clearInterval(interval);
  }, [activeTab]);

  const fetchTicketsOnly = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase.from('habitat_maintenance_tickets')
      .select('*, listing:habitat_listings(address)')
      .eq('tenant_id', user.id)
      .order('created_at', { ascending: false });
    
    if (data) {
      setTickets(data);
      setTrackingTicket((current: any) => {
        if (current) {
          const updated = data.find(t => t.id === current.id);
          return updated ? { ...current, ...updated } : current;
        }
        return current;
      });
    }
  };

  const checkStripeRedirect = () => {
    const queryParams = new URLSearchParams(window.location.search);
    if (queryParams.get('rent_success') === 'true') {
      alert("🎉 Paiement de loyer réussi ! Votre reçu vous sera envoyé par courriel.");
      window.history.replaceState(null, '', window.location.pathname);
    } else if (queryParams.get('rent_canceled') === 'true') {
      window.history.replaceState(null, '', window.location.pathname);
    }
  };

  const fetchData = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      setCurrentUser(user);
      
      const { data: profile } = await supabase.from('habitat_profiles').select('*').eq('id', user.id).maybeSingle();
      if (profile) {
        setProfileData({
          full_name: profile.full_name || 'Locataire',
          phone: profile.phone || '',
          address: profile.address || '',
          city: profile.city || '',
          country: profile.country || 'Canada',
          postal_code: profile.postal_code || '',
          is_address_private: profile.is_address_private !== false
        });
      }

      const { data: apps } = await supabase
        .from('habitat_applications')
        .select(`*, listing:habitat_listings(id, title, city, monthly_rent, available_from, images, address)`)
        .eq('tenant_id', user.id)
        .order('created_at', { ascending: false });

      if (apps) {
        setApplications(apps);
        const filteredContracts = apps.filter(app => 
          app.status === 'approved' || app.tenant_signed_at || app.landlord_signed_at
        );
        setContracts(filteredContracts);
      }

      const { data: screenings } = await supabase
        .from('habitat_screenings')
        .select(`*, landlord:habitat_profiles!habitat_screenings_landlord_id_fkey(full_name)`)
        .or(`tenant_email.eq.${user.email},tenant_id.eq.${user.id}`)
        .eq('status', 'pending_tenant_consent'); 
        
      if (screenings) setPendingScreenings(screenings);

      const { data: docs } = await supabase.from('habitat_documents').select('*').eq('tenant_id', user.id);
      if (docs) setDocuments(docs);

      const { data: favs } = await supabase.from('habitat_favorites').select(`listing:habitat_listings(id, title, city, monthly_rent, available_from, images, bedrooms, bathrooms)`).eq('tenant_id', user.id).order('created_at', { ascending: false });
      if (favs) setFavoriteListings(favs.map(f => f.listing).filter(Boolean)); 

      await fetchTicketsOnly();
    }
    setLoading(false);
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingProfile(true);
    if (!currentUser) return;
    const { error } = await supabase.from('habitat_profiles').update({
      phone: profileData.phone, address: profileData.address, city: profileData.city, country: profileData.country, postal_code: profileData.postal_code, is_address_private: profileData.is_address_private
    }).eq('id', currentUser.id);
    setSavingProfile(false);
    if (!error) alert("Profil mis à jour !");
    else alert("Erreur : " + error.message);
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>, docType: string) => {
    if (!e.target.files || e.target.files.length === 0) return;
    setUploading(true);
    const file = e.target.files[0];
    if (!currentUser) return;
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${currentUser.id}/${docType}_${Math.random()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage.from('habitat_tenant_docs').upload(fileName, file);
      if (uploadError) throw uploadError;
      
      const { data: { publicUrl } } = supabase.storage.from('habitat_tenant_docs').getPublicUrl(fileName);
      await supabase.from('habitat_documents').insert([{ tenant_id: currentUser.id, document_type: docType, file_url: publicUrl, file_name: file.name }]);
      
      alert('Document téléversé avec succès dans votre coffre-fort !');
      fetchData(); 
    } catch (error: any) {
      alert("Erreur lors de l'upload : " + error.message);
    } finally {
      setUploading(false);
    }
  };

  const deleteDocument = async (id: string) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    await supabase.from('habitat_documents').delete().eq('id', id);
    fetchData();
  };

  const removeFavorite = async (e: React.MouseEvent, listingId: string) => {
    e.stopPropagation();
    if (!currentUser) return;
    await supabase.from('habitat_favorites').delete().match({ tenant_id: currentUser.id, listing_id: listingId });
    setFavoriteListings(prev => prev.filter(l => l.id !== listingId));
  };

  const submitTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const currentContract = contracts.find(c => c.listing_id === ticketForm.listing_id) || contracts[0];

    if (!currentContract || !currentContract.listing_id || !currentContract.landlord_id) {
      alert("Erreur : Impossible d'identifier le propriétaire de ce logement.");
      return;
    }

    if (!ticketForm.description) {
      alert("Veuillez décrire le problème.");
      return;
    }
    
    setIsSubmittingTicket(true);
    const autoTitle = `Intervention : ${ticketForm.category.toUpperCase()}`;
    
    const { error } = await supabase.from('habitat_maintenance_tickets').insert([{
      tenant_id: currentUser.id,
      landlord_id: currentContract.landlord_id,
      listing_id: currentContract.listing_id, 
      title: autoTitle,
      category: ticketForm.category,
      urgency: ticketForm.urgency,
      description: ticketForm.description,
      status: 'open',
      tracking_status: 'none'
    }]);

    setIsSubmittingTicket(false);

    if (!error) {
      alert("Demande de service envoyée au propriétaire !");
      setShowTicketModal(false);
      setTicketForm({ ...ticketForm, description: '' });
      fetchData();
    } else {
      alert("Erreur lors de l'envoi : " + error.message);
    }
  };
  const handlePayRent = async (app: any) => {
    if (
      !app ||
      typeof app.id !== 'string'
    ) {
      alert(
        'La demande de location est invalide.',
      );
      return;
    }

    setIsPayingRent(app.id);

    try {
      const {
        data: sessionData,
        error: sessionError,
      } = await supabase.auth.getSession();

      if (
        sessionError ||
        !sessionData.session
      ) {
        throw new Error(
          'Session expirée. Veuillez vous reconnecter.',
        );
      }

      const paymentPeriod =
        currentHabitatPaymentPeriod();

      const {
        data: intentResult,
        error: intentError,
      } = await supabase.rpc(
        'create_habitat_rent_payment_intent',
        {
          p_application_id:
            app.id,
          p_payment_period:
            paymentPeriod,
        },
      );

      if (intentError) {
        throw intentError;
      }

      const checkoutUrl =
        await buildOfficialHabitatCheckoutHandoffUrl(
          'habitat_rent_payment',
          intentResult,
        );

      window.location.assign(
        checkoutUrl,
      );
    } catch (err: unknown) {
      console.error(
        'Erreur de paiement:',
        err,
      );

      const message =
        err instanceof Error
          ? err.message
          : 'Erreur inconnue';

      alert(
        "Une erreur est survenue lors de l'initialisation du paiement : "
          + message,
      );
    } finally {
      setIsPayingRent(null);
    }
  };


  const acceptVerification = async (screeningId: string) => {
    setProcessingAuthId(screeningId);
    try {
      await supabase.from('habitat_screenings').update({ tenant_id: currentUser.id, status: 'processing' }).eq('id', screeningId);
      const returnUrl = window.location.origin + window.location.pathname + "?consent_success=true";
      const { data, error } = await supabase.functions.invoke('trigger-certn-workflow', {
        body: { screening_id: screeningId, return_url: returnUrl }
      });
      
      if (error) throw error;
      if (data?.redirect_url) window.location.href = data.redirect_url; 
      else { alert("Enquête lancée !"); fetchData(); }
    } catch (err: any) {
      alert("Erreur lors de l'autorisation : " + err.message);
    } finally {
      setProcessingAuthId(null);
    }
  };

  const openChat = async (appId: string) => {
    setChatAppId(appId);
    const { data } = await supabase.from('habitat_messages').select('*').eq('application_id', appId).order('created_at', { ascending: true });
    if (data) setMessages(data);
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !currentUser || !chatAppId) return;
    const app = applications.find(a => a.id === chatAppId);
    const msgContent = newMessage.trim();
    const msg = { application_id: chatAppId, sender_id: currentUser.id, receiver_id: app.landlord_id, content: msgContent };
    setMessages([...messages, { ...msg, created_at: new Date().toISOString() }]);
    setNewMessage('');
    await supabase.from('habitat_messages').insert([msg]);
    openChat(chatAppId);
  };

  const getStatusLevel = (status: string) => {
    switch (status) { case 'new': return 1; case 'docs': return 2; case 'visit': return 3; case 'approved': return 4; default: return 1; }
  };
  
  const hasDoc = (type: string) => documents.find(d => d.document_type === type);

  const activeTrackingTickets = tickets.filter(t => (t.status === 'in_progress' || t.status === 'resolved') && !t.is_archived);

  return (
    <div className="min-h-screen bg-slate-50 font-sans pb-20 selection:bg-teal-500 selection:text-white">
      <Header />
      <main className="max-w-6xl mx-auto px-4 pt-32 pb-16">
        <button onClick={() => navigate('/')} className="mb-6 flex items-center gap-2 text-slate-500 hover:text-teal-600 font-bold transition">
          <ArrowLeft size={16} /> Retour au menu principal
        </button>

        {pendingScreenings.length > 0 && (
          <div className="mb-8 space-y-4 animate-in fade-in slide-in-from-top-4">
            {pendingScreenings.map(screening => (
              <div key={screening.id} className="bg-slate-900 rounded-[2rem] p-6 md:p-8 text-white relative overflow-hidden shadow-2xl border border-teal-500/30">
                <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/20 rounded-full blur-[80px] pointer-events-none"></div>
                <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-amber-400 font-bold text-sm uppercase tracking-widest mb-2"><AlertTriangle size={18} /> Action requise</div>
                    <h3 className="text-2xl font-black mb-2">Autorisation d'enquête de crédit</h3>
                    <p className="text-slate-300 text-sm mb-4"><strong>{screening.landlord?.full_name || "Un propriétaire"}</strong> souhaite vérifier votre dossier financier et criminel.</p>
                    <p className="text-[10px] text-slate-400">Conformément à la Loi 25, cette étape requiert votre consentement formel et une vérification d'identité.</p>
                  </div>
                  <button 
                    onClick={() => acceptVerification(screening.id)} 
                    disabled={processingAuthId === screening.id} 
                    className="bg-teal-400 text-slate-900 font-black px-8 py-4 rounded-xl hover:scale-105 transition-transform flex items-center gap-2 shadow-lg"
                  >
                    {processingAuthId === screening.id ? <><Loader2 className="animate-spin" size={18} /> Connexion...</> : 'Vérifier mon identité'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            {/* ✅ CLOCHE DE NOTIFICATION INTÉGRÉE ICI AVEC TAILLE FIXE */}
            <div className="flex items-center gap-4 mb-2">
              <h1 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tight">Bonjour, {profileData.full_name.split(' ')[0]}</h1>
              <div className="bg-white shadow-sm border border-slate-200 rounded-full w-10 h-10 flex items-center justify-center relative shrink-0">
                <NotificationBell />
              </div>
            </div>
            <p className="text-slate-500 md:text-lg font-medium">Gérez vos candidatures et votre vie locative.</p>
          </div>
          <button onClick={() => navigate('/habitat')} className="bg-teal-500 text-white px-8 py-4 rounded-2xl font-black uppercase tracking-widest shadow-lg shadow-teal-500/30 hover:bg-slate-900 transition-all hover:scale-105 flex items-center justify-center gap-3 shrink-0">
            <Search size={20} /> Explorer les logements
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-10 p-1.5 bg-white border border-slate-200 rounded-2xl w-fit shadow-sm">
          <button onClick={() => setActiveTab('applications')} className={`px-5 py-3 font-bold text-sm rounded-xl transition-all flex items-center gap-2 ${activeTab === 'applications' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-50'}`}><Clock size={18} /> Candidatures</button>
          <button onClick={() => setActiveTab('carte')} className={`px-5 py-3 font-bold text-sm rounded-xl transition-all flex items-center gap-2 ${activeTab === 'carte' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-50'}`}><Map size={18} /> Carte</button>
          <button onClick={() => setActiveTab('contrats')} className={`px-5 py-3 font-bold text-sm rounded-xl transition-all flex items-center gap-2 ${activeTab === 'contrats' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-50'}`}><FileCheck size={18} /> Mes Contrats ({contracts.length})</button>
          <button onClick={() => setActiveTab('maintenance')} className={`px-5 py-3 font-bold text-sm rounded-xl transition-all flex items-center gap-2 ${activeTab === 'maintenance' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-50'}`}><Wrench size={18} /> Maintenance</button>
          <button onClick={() => setActiveTab('suivi')} className={`px-5 py-3 font-bold text-sm rounded-xl transition-all flex items-center gap-2 ${activeTab === 'suivi' ? 'bg-green-600 text-white' : 'text-slate-500 hover:bg-slate-50'}`}><Truck size={18} /> Suivre l'artisan</button>
          <button onClick={() => setActiveTab('favorites')} className={`px-5 py-3 font-bold text-sm rounded-xl transition-all flex items-center gap-2 ${activeTab === 'favorites' ? 'bg-red-500 text-white' : 'text-slate-500 hover:bg-slate-50'}`}><Heart size={18} /> Favoris</button>
          <button onClick={() => setActiveTab('documents')} className={`px-5 py-3 font-bold text-sm rounded-xl transition-all flex items-center gap-2 ${activeTab === 'documents' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-50'}`}><Shield size={18} /> Coffre-Fort</button>
          <button onClick={() => setActiveTab('profil')} className={`px-5 py-3 font-bold text-sm rounded-xl transition-all flex items-center gap-2 ${activeTab === 'profil' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-50'}`}><User size={18} /> Profil</button>
        </div>

        {activeTab === 'carte' && (
          <div className="animate-in fade-in slide-in-from-bottom-8 bg-white rounded-[2.5rem] border border-slate-200 p-6 md:p-8 shadow-sm relative z-0">
            <h3 className="text-2xl font-black text-slate-900 mb-2 flex items-center gap-2"><Map className="text-teal-500" /> Découvrez le quartier</h3>
            <p className="text-slate-500 mb-6 font-medium">Explorez les écoles, commerces et points d'intérêts autour de vos annonces.</p>
            <div className="h-[60vh] rounded-3xl overflow-hidden relative border border-slate-100">
              <InteractiveMap />
            </div>
          </div>
        )}

        {activeTab === 'suivi' && (
          <div className="animate-in fade-in slide-in-from-bottom-8 duration-500 space-y-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl font-black text-slate-900">Suivi des Interventions</h2>
                <p className="text-slate-500 font-medium">Suivez l'arrivée de l'artisan en direct sur la carte.</p>
              </div>
            </div>

            {activeTrackingTickets.length === 0 ? (
              <div className="bg-white border-2 border-dashed border-slate-200 rounded-[3rem] p-24 text-center">
                <Truck size={40} className="mx-auto text-slate-200 mb-4" />
                <h3 className="text-xl font-black text-slate-800 mb-2">Aucune intervention en cours</h3>
                <p className="text-slate-400 font-medium">Le suivi GPS s'affichera ici dès que votre propriétaire aura autorisé un artisan.</p>
              </div>
            ) : (
              <div className="grid gap-8">
                {activeTrackingTickets.map(ticket => (
                  <div key={ticket.id} className="bg-white rounded-[2.5rem] shadow-xl border border-slate-200 overflow-hidden">
                    <div className="p-6 md:p-8 bg-slate-50 border-b border-slate-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-green-100 text-green-600 rounded-2xl flex items-center justify-center shrink-0 border border-green-200">
                          <Truck size={28}/>
                        </div>
                        <div>
                          <h3 className="text-xl font-black text-slate-900">{ticket.pro_name || 'Artisan Forfeo'}</h3>
                          <p className="text-sm font-bold text-slate-500 flex items-center gap-1"><MapPin size={14}/> Destination : {ticket.listing?.address}</p>
                        </div>
                      </div>
                      <div className="text-right w-full md:w-auto">
                        <span className={`inline-block px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest border ${ticket.tracking_status === 'en_route' ? 'bg-green-100 text-green-700 border-green-200 animate-pulse' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                          {ticket.tracking_status === 'en_route' ? 'En Route 📍' : 'En préparation'}
                        </span>
                      </div>
                    </div>

                    <div className="h-[400px] md:h-[500px] w-full bg-slate-100 relative">
                      {ticket.tracking_status === 'en_route' && ticket.pro_lat && ticket.pro_lng ? (
                        <iframe 
                          width="100%" 
                          height="100%" 
                          frameBorder="0" 
                          scrolling="no" 
                          marginHeight={0} 
                          marginWidth={0} 
                          src={`https://www.openstreetmap.org/export/embed.html?bbox=${ticket.pro_lng - 0.01}%2C${ticket.pro_lat - 0.01}%2C${ticket.pro_lng + 0.01}%2C${ticket.pro_lat + 0.01}&layer=mapnik&marker=${ticket.pro_lat}%2C${ticket.pro_lng}`} 
                          style={{ border: 0 }}
                        ></iframe>
                      ) : (
                        <div className="flex items-center justify-center h-full flex-col text-slate-400 p-8 text-center">
                          <Loader2 className="animate-spin w-12 h-12 mb-4 text-slate-300"/>
                          <p className="font-black text-xl text-slate-500 mb-2">En attente du départ de l'artisan...</p>
                          <p className="text-sm font-medium">La carte s'affichera automatiquement ici dès que {ticket.pro_name || "l'artisan"} aura activé son GPS.</p>
                        </div>
                      )}
                    </div>
                    
                    <div className="p-6 bg-white border-t border-slate-100">
                      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <p className="text-sm font-bold text-slate-600">Intervention : <span className="text-slate-900">{ticket.category.toUpperCase()}</span></p>
                        <p className="text-xs font-black text-blue-600 uppercase tracking-widest bg-blue-50 px-4 py-2 rounded-lg">Pris en charge par le propriétaire</p>
                      </div>
                    </div>

                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'maintenance' && (
          <div className="animate-in fade-in slide-in-from-bottom-8 duration-500 space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-3xl border border-slate-200 shadow-sm mb-6">
              <div>
                <h3 className="text-2xl font-black text-slate-900 flex items-center gap-2"><Wrench className="text-teal-500"/> Centre de Services</h3>
                <p className="text-slate-500 font-medium">Signalez un problème technique à votre propriétaire.</p>
              </div>
              <button 
                onClick={() => {
                  if(contracts.length > 0 && !ticketForm.listing_id) {
                    setTicketForm({...ticketForm, listing_id: contracts[0].listing_id});
                  }
                  setShowTicketModal(true);
                }}
                disabled={contracts.length === 0}
                className={`px-6 py-3 rounded-xl font-black text-sm uppercase tracking-widest transition-all flex items-center gap-2 ${contracts.length > 0 ? 'bg-slate-900 text-white hover:bg-teal-600 shadow-lg' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
              >
                <PlusCircle size={18} /> Demander un service
              </button>
            </div>

            {contracts.length === 0 ? (
              <div className="bg-amber-50 border border-amber-200 p-6 rounded-2xl flex items-start gap-4">
                <AlertTriangle className="text-amber-500 shrink-0" size={24}/>
                <div>
                  <p className="font-bold text-amber-800">Aucun bail actif détecté.</p>
                  <p className="text-amber-700 text-sm">Vous devez avoir signé un bail avec un propriétaire sur Forfeo pour pouvoir utiliser le centre de services.</p>
                </div>
              </div>
            ) : tickets.length === 0 ? (
              <div className="bg-white border-2 border-dashed border-slate-200 rounded-[2.5rem] p-20 text-center flex flex-col items-center">
                <Wrench className="w-16 h-16 text-slate-300 mb-4" />
                <p className="text-slate-500 font-bold text-lg">Aucune demande de service en cours.</p>
                <p className="text-slate-400 mt-2">Tout semble fonctionner parfaitement dans votre logement !</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {tickets.map(ticket => (
                  <div key={ticket.id} className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between gap-6 hover:shadow-md transition-shadow">
                    <div className="flex gap-4 w-full md:w-auto">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${ticket.status === 'open' ? 'bg-amber-100 text-amber-600' : ticket.status === 'searching_pro' ? 'bg-purple-100 text-purple-600' : ticket.status === 'in_progress' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'}`}>
                        {ticket.status === 'resolved' ? <CheckCircle size={20}/> : <Wrench size={20}/>}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-black uppercase tracking-widest text-slate-400">{ticket.category}</span>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${ticket.urgency === 'urgente' ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-500'}`}>{ticket.urgency}</span>
                        </div>
                        <h4 className="text-lg font-bold text-slate-900 mb-2">{ticket.description}</h4>
                        <p className="text-xs text-slate-500 flex items-center gap-1"><MapPin size={12}/> {ticket.listing?.address}</p>
                        
                        {(ticket.status === 'in_progress' || ticket.tracking_status === 'en_route') && (
                          <button 
                            onClick={() => setActiveTab('suivi')}
                            className="mt-4 bg-slate-900 text-white px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-teal-500 transition-colors flex items-center gap-2 shadow-lg shadow-slate-900/20"
                          >
                            <Truck size={16}/> Suivre l'artisan
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end justify-center border-t md:border-t-0 md:border-l border-slate-100 pt-4 md:pt-0 md:pl-6 shrink-0">
                      <p className="text-[10px] font-black uppercase text-slate-400 mb-1">Statut de la demande</p>
                      <p className={`font-black uppercase text-sm ${ticket.status === 'open' ? 'text-amber-500' : ticket.status === 'searching_pro' ? 'text-purple-500' : ticket.status === 'in_progress' ? 'text-blue-500' : 'text-green-500'}`}>
                        {ticket.status === 'open' ? 'En attente' : ticket.status === 'searching_pro' ? 'Envoi aux artisans' : ticket.status === 'in_progress' ? 'En réparation' : 'Résolu'}
                      </p>
                      <p className="text-[10px] text-slate-400 mt-2">Signalé le {new Date(ticket.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'contrats' && (
          <div className="animate-in fade-in slide-in-from-bottom-8 duration-500 space-y-6">
            {contracts.length === 0 ? (
              <div className="bg-white border-2 border-dashed border-slate-200 rounded-[2.5rem] p-20 text-center flex flex-col items-center">
                <FileText className="w-16 h-16 text-slate-300 mb-4" />
                <p className="text-slate-500 font-bold mb-6 text-lg">Aucun contrat ou bail n'est encore disponible.</p>
                <button onClick={() => navigate('/habitat')} className="bg-slate-900 text-white px-8 py-4 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-teal-600 transition-colors">
                  Trouver mon logement
                </button>
              </div>
            ) : (
              contracts.map(app => {
                const isFullySigned = app.tenant_signed_at && app.landlord_signed_at;
                return (
                  <div key={app.id} className="bg-slate-900 text-white p-8 rounded-[2.5rem] shadow-xl border border-slate-800 flex flex-col lg:flex-row justify-between items-center gap-8 relative overflow-hidden group">
                    {isFullySigned && <div className="absolute top-0 right-0 w-32 h-32 bg-teal-500/10 blur-3xl rounded-full -mr-16 -mt-16 group-hover:bg-teal-500/20 transition-all"></div>}
                    <div className="flex items-center gap-6 relative z-10 w-full lg:w-auto">
                      <div className={`w-16 h-16 shrink-0 rounded-2xl flex items-center justify-center border ${isFullySigned ? 'bg-teal-500/20 border-teal-500/50 text-teal-400' : 'bg-white/5 border-white/10 text-white/50'}`}>
                        <FileText size={32} />
                      </div>
                      <div>
                        <h3 className="text-xl font-black tracking-tight flex items-center gap-2">{app.listing?.address} {isFullySigned && <ShieldCheck size={18} className="text-teal-400" />}</h3>
                        <div className="flex flex-wrap items-center gap-3 mt-1">
                          <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${isFullySigned ? 'bg-teal-500 text-slate-900' : 'bg-amber-500 text-slate-900'}`}>{isFullySigned ? 'Contrat Scellé' : 'Signature Requise'}</span>
                          <span className="text-slate-500 text-[10px] font-bold uppercase tracking-widest">Réf: {app.id.substring(0,8)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto relative z-10">
                      {isFullySigned ? (
                        <>
                          <button onClick={() => navigate(`/habitat/lease/${app.id}`)} className="w-full sm:w-auto bg-white text-slate-900 px-6 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-teal-50 transition-all flex items-center justify-center gap-2"><Eye size={16}/> Voir</button>
                          <button onClick={() => window.open(`/habitat/lease/${app.id}?download=true`, '_blank')} className="w-full sm:w-auto bg-slate-800 text-white px-6 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-700 transition-all flex items-center justify-center gap-2 border border-slate-700"><FileDown size={16}/> PDF</button>
                        </>
                      ) : (
                        <button onClick={() => navigate(`/habitat/lease/${app.id}`)} className="w-full sm:w-auto bg-teal-600 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-teal-500 transition-all flex items-center justify-center gap-2">Signer le bail</button>
                      )}
                      
                      {isFullySigned && (
                        <button onClick={() => handlePayRent(app)} disabled={isPayingRent === app.id} className="w-full sm:w-auto bg-amber-500 text-slate-900 px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-amber-400 transition-all flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20 disabled:opacity-70 ml-2">
                          {isPayingRent === app.id ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />} Payer {app.listing?.monthly_rent} $
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {activeTab === 'applications' && (
          <div className="animate-in fade-in slide-in-from-bottom-8 space-y-6">
            {applications.length === 0 ? (
              <div className="bg-white border-2 border-dashed border-slate-200 rounded-[2.5rem] p-20 text-center flex flex-col items-center">
                <Home className="w-16 h-16 text-slate-300 mb-4" />
                <p className="text-slate-500 font-bold mb-6 text-lg">Vous n'avez postulé à aucun logement pour le moment.</p>
                <button onClick={() => navigate('/habitat')} className="bg-slate-900 text-white px-8 py-4 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-teal-600 transition-colors">Explorer les annonces</button>
              </div>
            ) : (
              applications.map(app => {
                const level = getStatusLevel(app.status);
                return (
                  <div key={app.id} className="bg-white rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden group hover:border-teal-200 transition-colors flex flex-col md:flex-row">
                    <div className="md:w-1/3 relative h-48 md:h-auto overflow-hidden bg-slate-100 cursor-pointer" onClick={() => navigate(`/habitat/logement/${app.listing?.id}`)}>
                      <img src={app.listing?.images?.[0] || ''} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" alt="Logement" />
                    </div>
                    <div className="p-8 md:w-2/3">
                      <div className="flex justify-between items-start mb-6">
                        <div>
                          <h3 className="text-2xl font-black text-slate-900 cursor-pointer hover:text-teal-600 transition-colors" onClick={() => navigate(`/habitat/logement/${app.listing?.id}`)}>{app.listing?.title}</h3>
                          <p className="text-sm text-slate-500 font-medium">Envoyée le {new Date(app.created_at).toLocaleDateString()}</p>
                        </div>
                        <div className="text-2xl font-black text-teal-600">{app.listing?.monthly_rent} $</div>
                      </div>
                      <button onClick={() => openChat(app.id)} className="bg-slate-900 text-white px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest flex items-center gap-3 mb-6 hover:bg-teal-500 transition-all"><MessageSquare size={18} /> Chat avec le propriétaire</button>
                      <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                        <div className="flex justify-between mb-6"><h4 className="text-xs font-black uppercase text-slate-400 tracking-widest">Statut du dossier</h4><span className="text-xs font-bold text-teal-700">{app.status.toUpperCase()}</span></div>
                        <div className="relative">
                          <div className="absolute top-5 w-full h-1 bg-slate-200 rounded-full"></div>
                          <div className={`absolute top-5 h-1 bg-teal-500 rounded-full transition-all duration-1000 ${level === 1 ? 'w-1/4' : level === 2 ? 'w-1/2' : level === 3 ? 'w-3/4' : 'w-full'}`}></div>
                          <div className="relative flex justify-between">
                            {[1, 2, 3, 4].map(l => <div key={l} className={`w-10 h-10 rounded-full flex items-center justify-center border-4 border-white ${level >= l ? 'bg-teal-500 text-white' : 'bg-slate-200'}`}><CheckCircle size={16} /></div>)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="animate-in fade-in slide-in-from-bottom-8 grid md:grid-cols-2 gap-6">
            <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm flex flex-col items-center justify-center text-center h-[300px]">
              <input type="file" ref={idFileInput} onChange={(e) => handleUpload(e, 'id_card')} className="hidden" accept=".pdf,image/*" />
              {hasDoc('id_card') ? (
                <><div className="w-16 h-16 bg-green-50 text-green-500 rounded-2xl flex items-center justify-center mb-4"><FileCheck size={32}/></div><h3 className="font-black">Pièce d'identité</h3><div className="flex gap-2 mt-4"><a href={hasDoc('id_card')?.file_url} target="_blank" className="bg-slate-100 px-4 py-2 rounded-lg text-xs font-black">VOIR</a><button onClick={() => deleteDocument(hasDoc('id_card')?.id)} className="text-red-500 hover:bg-red-50 p-2 rounded-lg transition-colors"><Trash2 size={18}/></button></div></>
              ) : (
                <><FileText className="text-slate-300 mb-4" size={48} /><h3 className="font-black mb-6">Pièce d'identité</h3><button onClick={() => idFileInput.current?.click()} disabled={uploading} className="bg-slate-900 text-white px-8 py-3 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-teal-600 transition-colors">{uploading ? 'Envoi...' : 'Téléverser'}</button></>
              )}
            </div>
            <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm flex flex-col items-center justify-center text-center h-[300px]">
              <input type="file" ref={incomeFileInput} onChange={(e) => handleUpload(e, 'income_proof')} className="hidden" accept=".pdf,image/*" />
              {hasDoc('income_proof') ? (
                <><div className="w-16 h-16 bg-green-50 text-green-500 rounded-2xl flex items-center justify-center mb-4"><FileCheck size={32}/></div><h3 className="font-black">Preuve de revenus</h3><div className="flex gap-2 mt-4"><a href={hasDoc('income_proof')?.file_url} target="_blank" className="bg-slate-100 px-4 py-2 rounded-lg text-xs font-black">VOIR</a><button onClick={() => deleteDocument(hasDoc('income_proof')?.id)} className="text-red-500 hover:bg-red-50 p-2 rounded-lg transition-colors"><Trash2 size={18}/></button></div></>
              ) : (
                <><FileText className="text-slate-300 mb-4" size={48} /><h3 className="font-black mb-6">Preuve de revenus</h3><button onClick={() => incomeFileInput.current?.click()} disabled={uploading} className="bg-slate-900 text-white px-8 py-3 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-teal-600 transition-colors">{uploading ? 'Envoi...' : 'Téléverser'}</button></>
              )}
            </div>
          </div>
        )}

        {activeTab === 'profil' && (
          <form onSubmit={handleSaveProfile} className="bg-white rounded-[2.5rem] border border-slate-200 p-8 md:p-12 max-w-2xl animate-in fade-in slide-in-from-bottom-8">
            <h2 className="text-3xl font-black mb-8">Mon Profil Locataire</h2>
            <div className="space-y-6">
              <div><label className="text-xs font-black text-slate-400 uppercase">Téléphone</label><input type="tel" value={profileData.phone} onChange={e => setProfileData({...profileData, phone: e.target.value})} className="w-full p-4 bg-slate-50 rounded-xl border font-bold" /></div>
              <div><label className="text-xs font-black text-slate-400 uppercase">Adresse actuelle</label><input type="text" value={profileData.address} onChange={e => setProfileData({...profileData, address: e.target.value})} className="w-full p-4 bg-slate-50 rounded-xl border font-bold" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="text-xs font-black text-slate-400 uppercase">Ville</label><input type="text" value={profileData.city} onChange={e => setProfileData({...profileData, city: e.target.value})} className="w-full p-4 bg-slate-50 rounded-xl border font-bold" /></div>
                <div><label className="text-xs font-black text-slate-400 uppercase">Code Postal</label><input type="text" value={profileData.postal_code} onChange={e => setProfileData({...profileData, postal_code: e.target.value})} className="w-full p-4 bg-slate-50 rounded-xl border font-bold" /></div>
              </div>
              <button type="submit" disabled={savingProfile} className="w-full bg-teal-500 text-white py-5 rounded-xl font-black uppercase hover:bg-teal-600 transition shadow-lg">{savingProfile ? 'Sauvegarde...' : 'Enregistrer'}</button>
            </div>
          </form>
        )}

        {activeTab === 'favorites' && (
          <div className="animate-in fade-in slide-in-from-bottom-8">
            {favoriteListings.length === 0 ? (
              <div className="bg-white border-2 border-dashed border-slate-200 rounded-[2.5rem] p-20 text-center flex flex-col items-center">
                <Heart className="w-16 h-16 text-slate-300 mb-4" />
                <p className="text-slate-500 font-bold mb-6 text-lg">Vous n'avez pas encore de coup de cœur.</p>
                <button onClick={() => navigate('/habitat')} className="bg-slate-900 text-white px-8 py-4 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-teal-600 transition-colors">Découvrir les logements</button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {favoriteListings.map(listing => (
                  <div key={listing.id} onClick={() => navigate(`/habitat/logement/${listing.id}`)} className="bg-white rounded-[2rem] border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl cursor-pointer group relative">
                    <button onClick={(e) => removeFavorite(e, listing.id)} className="absolute top-4 right-4 z-20 p-2.5 bg-white/90 rounded-full shadow-md hover:bg-white transition-colors"><Heart className="w-5 h-5 fill-red-500 text-red-500" /></button>
                    <div className="h-48 overflow-hidden"><img src={listing.images?.[0] || ''} className="w-full h-full object-cover group-hover:scale-110 transition-duration-1000" /></div>
                    <div className="p-6"><h3 className="text-lg font-black">{listing.title}</h3><p className="text-teal-600 font-bold">{listing.monthly_rent} $ / mois</p></div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </main>

      {/* ✅ MODAL DE CRÉATION DE TICKET (LOCATAIRE) */}
      {showTicketModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-md animate-in fade-in">
          <div className="bg-white rounded-[2.5rem] w-full max-w-lg shadow-2xl overflow-hidden border border-slate-200">
            <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-teal-50">
              <div>
                <h3 className="text-2xl font-black text-slate-900 flex items-center gap-2"><Wrench size={24} className="text-teal-600"/> Demande de Service</h3>
                <p className="text-sm font-bold text-teal-600 mt-1">Signalez un problème au propriétaire</p>
              </div>
              <button onClick={() => setShowTicketModal(false)} className="p-2 bg-white rounded-full text-slate-400 hover:text-slate-900 shadow-sm"><X size={20}/></button>
            </div>
            <form onSubmit={submitTicket} className="p-8 space-y-6">
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Logement concerné</label>
                <select 
                  value={ticketForm.listing_id} 
                  onChange={e => setTicketForm({...ticketForm, listing_id: e.target.value})} 
                  className="w-full p-4 bg-slate-50 rounded-xl border border-slate-200 font-bold text-slate-900 focus:ring-2 focus:ring-teal-500 outline-none"
                >
                  {contracts.map(c => (
                    <option key={c.listing_id} value={c.listing_id}>{c.listing?.address}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Catégorie</label>
                  <select 
                    value={ticketForm.category} 
                    onChange={e => setTicketForm({...ticketForm, category: e.target.value})} 
                    className="w-full p-4 bg-slate-50 rounded-xl border border-slate-200 font-bold text-slate-900 focus:ring-2 focus:ring-teal-500 outline-none cursor-pointer"
                  >
                    <option value="plomberie">Plomberie</option>
                    <option value="electricite">Électricité</option>
                    <option value="chauffage">Chauffage / Clim</option>
                    <option value="electromenager">Électroménager</option>
                    <option value="nuisibles">Nuisibles</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Urgence</label>
                  <select 
                    value={ticketForm.urgency} 
                    onChange={e => setTicketForm({...ticketForm, urgency: e.target.value})} 
                    className="w-full p-4 bg-slate-50 rounded-xl border border-slate-200 font-bold text-slate-900 focus:ring-2 focus:ring-teal-500 outline-none cursor-pointer"
                  >
                    <option value="basse">Basse</option>
                    <option value="normale">Normale</option>
                    <option value="urgente">Urgente 🚨</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Description du problème</label>
                <textarea 
                  required 
                  placeholder="Expliquez en détail ce qui ne fonctionne pas..."
                  value={ticketForm.description} 
                  onChange={e => setTicketForm({...ticketForm, description: e.target.value})} 
                  className="w-full p-4 bg-slate-50 rounded-xl border border-slate-200 font-bold text-slate-900 focus:ring-2 focus:ring-teal-500 outline-none h-32 resize-none" 
                />
              </div>
              
              <button type="submit" disabled={isSubmittingTicket} className="w-full bg-slate-900 text-white py-5 rounded-2xl font-black uppercase tracking-widest hover:bg-teal-600 transition-colors shadow-lg flex items-center justify-center gap-2">
                {isSubmittingTicket ? 'Envoi...' : 'Soumettre le ticket'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* CHAT MODAL */}
      {chatAppId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white rounded-[2rem] w-full max-w-lg h-[70vh] shadow-2xl flex flex-col overflow-hidden">
            <div className="p-6 bg-slate-900 text-white flex justify-between items-center">
              <h3 className="font-black flex items-center gap-2"><MessageSquare /> Chat Propriétaire</h3>
              <button onClick={() => setChatAppId(null)} className="hover:text-slate-300 transition-colors"><X size={20}/></button>
            </div>
            <div className="flex-1 p-6 overflow-y-auto space-y-4 bg-slate-50">
              {messages.map((msg, idx) => (
                <div key={idx} className={`flex flex-col ${msg.sender_id === currentUser?.id ? 'items-end' : 'items-start'}`}>
                  <div className={`max-w-[80%] p-4 rounded-2xl text-sm ${msg.sender_id === currentUser?.id ? 'bg-teal-500 text-white rounded-tr-none' : 'bg-white border border-slate-200 text-slate-800 rounded-tl-none shadow-sm'}`}>{msg.content}</div>
                </div>
              ))}
            </div>
            <form onSubmit={sendMessage} className="p-4 bg-white border-t flex gap-2">
              <input type="text" value={newMessage} onChange={(e)=>setNewMessage(e.target.value)} placeholder="Message..." className="flex-1 bg-slate-100 rounded-xl px-4 py-4 focus:outline-none" />
              <button type="submit" className="bg-teal-500 text-white w-14 rounded-xl flex items-center justify-center hover:bg-teal-600 transition-colors"><Send size={20} /></button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
